<template>
  <div id="editor-container">
    <header-bar>
      <action icon="close" :label="$t('buttons.close')" @action="close()" />
      <title>{{ req.name }}</title>

      <action
        v-if="user.perm.modify"
        id="save-button"
        icon="save"
        :label="$t('buttons.save')"
        @action="save()"
      />
    </header-bar>

    <breadcrumbs base="/files" noLink />

    <!-- Use Tiptap editor for markdown files -->
    <tiptap-editor
      v-if="isMarkdownFile"
      :content="req.content"
      :read-only="!user.perm.modify"
      @update="handleTiptapUpdate"
      class="tiptap-container"
    />

    <!-- Use Ace editor for other files -->
    <form v-else id="editor"></form>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { files as api } from "@/api";
import { theme } from "@/utils/constants";
import buttons from "@/utils/buttons";
import url from "@/utils/url";

import ace from "ace-builds/src-min-noconflict/ace.js";
import modelist from "ace-builds/src-min-noconflict/ext-modelist.js";
import "ace-builds/webpack-resolver";

import HeaderBar from "@/components/header/HeaderBar";
import Action from "@/components/header/Action";
import Breadcrumbs from "@/components/Breadcrumbs";
import TiptapEditor from "@/components/TiptapEditor.vue";

export default {
  name: "editor",
  components: {
    HeaderBar,
    Action,
    Breadcrumbs,
    TiptapEditor,
  },
  data: function () {
    return {
      editor: null,
      tiptapContent: "",
    };
  },
  computed: {
    ...mapState(["req", "user"]),
    isMarkdownFile() {
      return this.req.name.toLowerCase().endsWith('.md');
    },
    breadcrumbs() {
      let parts = this.$route.path.split("/");

      if (parts[0] === "") {
        parts.shift();
      }

      if (parts[parts.length - 1] === "") {
        parts.pop();
      }

      let breadcrumbs = [];

      for (let i = 0; i < parts.length; i++) {
        breadcrumbs.push({ name: decodeURIComponent(parts[i]) });
      }

      breadcrumbs.shift();

      if (breadcrumbs.length > 3) {
        while (breadcrumbs.length !== 4) {
          breadcrumbs.shift();
        }

        breadcrumbs[0].name = "...";
      }

      return breadcrumbs;
    },
  },
  created() {
    window.addEventListener("keydown", this.keyEvent);
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.keyEvent);
    if (this.editor) {
      this.editor.destroy();
    }
  },
  mounted: function () {
    if (!this.isMarkdownFile) {
      this.initializeAceEditor();
    }
  },
  methods: {
    initializeAceEditor() {
      const fileContent = this.req.content || "";

      this.editor = ace.edit("editor", {
        value: fileContent,
        showPrintMargin: false,
        readOnly: this.req.type === "textImmutable",
        theme: "ace/theme/chrome",
        mode: modelist.getModeForPath(this.req.name).mode,
        wrap: true,
      });

      if (theme == "dark") {
        this.editor.setTheme("ace/theme/twilight");
      }
    },
    back() {
      let uri = url.removeLastDir(this.$route.path) + "/";
      this.$router.push({ path: uri });
    },
    keyEvent(event) {
      if (!event.ctrlKey && !event.metaKey) {
        return;
      }

      if (String.fromCharCode(event.which).toLowerCase() !== "s") {
        return;
      }

      event.preventDefault();
      this.save();
    },
    async save() {
      const button = "save";
      buttons.loading("save");

      try {
        const content = this.isMarkdownFile ? this.tiptapContent : this.editor.getValue();
        await api.put(this.$route.path, content);
        buttons.success(button);
      } catch (e) {
        buttons.done(button);
        this.$showError(e);
      }
    },
    close() {
      this.$store.commit("updateRequest", {});

      let uri = url.removeLastDir(this.$route.path) + "/";
      this.$router.push({ path: uri });
    },
    handleTiptapUpdate(content) {
      this.tiptapContent = content;
    },
  },
};
</script>
<style>
#editor-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: #fff;
}

#editor {
  flex: 1;
  min-height: 0;
}

.tiptap-container {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

</style>

